// utils/idCardValidator.js

/**
 * 身份证号码校验工具（简化版）
 */
export default {
  /**
   * 校验身份证号码
   * @param {String} idCard 身份证号码
   * @returns {Object} 返回校验结果 { isValid: boolean, message: string }
   */
  validate(idCard) {
    // 1. 空值判断
    if (!idCard) {
      return {
        isValid: false,
        message: '请输入身份证号码'
      };
    }

    // 2. 去除空格并转为大写
    idCard = idCard.toString().trim().toUpperCase();
    
    // 3. 长度校验
    if (idCard.length !== 15 && idCard.length !== 18) {
      return {
        isValid: false,
        message: '身份证号码长度应为15位或18位'
      };
    }

    // 4. 格式校验
    if (idCard.length === 18) {
      // 18位身份证：前17位数字，最后一位数字或X
      if (!/^\d{17}[\dX]$/.test(idCard)) {
        return {
          isValid: false,
          message: '身份证号码格式不正确'
        };
      }
      
      // 校验码验证
      if (!this._verifyCheckCode(idCard)) {
        return {
          isValid: false,
          message: '身份证校验码不正确'
        };
      }
    } else {
      // 15位身份证：全部为数字
      if (!/^\d{15}$/.test(idCard)) {
        return {
          isValid: false,
          message: '身份证号码格式不正确'
        };
      }
    }

    // 5. 出生日期校验
    const birthdayResult = this._validateBirthday(idCard);
    if (!birthdayResult.isValid) {
      return {
        isValid: false,
        message: birthdayResult.message
      };
    }

    // 6. 年龄校验
    const age = this._calculateAge(birthdayResult.year, birthdayResult.month, birthdayResult.day);
    if (age < 0 || age > 150) {
      return {
        isValid: false,
        message: '年龄超出合理范围(0-150岁)'
      };
    }

    return {
      isValid: true,
      message: '身份证号码合法'
    };
  },

  /**
   * 18位身份证校验码验证
   * @private
   */
  _verifyCheckCode(idCard) {
    if (idCard.length !== 18) return false;
    
    // 加权因子
    const weightFactors = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
    // 校验码对应值
    const checkCodes = ['1', '0', 'X', '9', '8', '7', '6', '5', '4', '3', '2'];
    
    let sum = 0;
    for (let i = 0; i < 17; i++) {
      sum += parseInt(idCard.charAt(i)) * weightFactors[i];
    }
    
    const mod = sum % 11;
    const checkCode = checkCodes[mod];
    
    return checkCode === idCard.charAt(17);
  },

  /**
   * 出生日期校验
   * @private
   */
  _validateBirthday(idCard) {
    let year = '';
    let month = '';
    let day = '';
    
    if (idCard.length === 18) {
      year = idCard.substr(6, 4);
      month = idCard.substr(10, 2);
      day = idCard.substr(12, 2);
    } else {
      // 15位身份证，年份前两位补19
      year = '19' + idCard.substr(6, 2);
      month = idCard.substr(8, 2);
      day = idCard.substr(10, 2);
    }
    
    // 转换为整数
    const y = parseInt(year);
    const m = parseInt(month);
    const d = parseInt(day);
    
    // 基本范围校验
    if (y < 1900 || y > new Date().getFullYear()) {
      return {
        isValid: false,
        message: '出生年份不合法'
      };
    }
    
    if (m < 1 || m > 12) {
      return {
        isValid: false,
        message: '出生月份不合法'
      };
    }
    
    // 月份天数校验
    const daysInMonth = new Date(y, m, 0).getDate();
    if (d < 1 || d > daysInMonth) {
      return {
        isValid: false,
        message: '出生日期不合法'
      };
    }
    
    // 不能是未来日期
    const birthDate = new Date(y, m - 1, d);
    const today = new Date();
    if (birthDate > today) {
      return {
        isValid: false,
        message: '出生日期不能是未来日期'
      };
    }
    
    return {
      isValid: true,
      year: year,
      month: month.padStart(2, '0'),
      day: day.padStart(2, '0')
    };
  },

  /**
   * 计算年龄
   * @private
   */
  _calculateAge(birthYear, birthMonth, birthDay) {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const currentDay = today.getDate();
    
    let age = currentYear - parseInt(birthYear);
    
    // 如果当前月份小于出生月份，或者月份相同但日期小于出生日期，则年龄减1
    if (currentMonth < parseInt(birthMonth) || 
        (currentMonth === parseInt(birthMonth) && currentDay < parseInt(birthDay))) {
      age--;
    }
    
    return age;
  }
};